import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { RedoOutlined, PlusOutlined, CloseOutlined, ExportOutlined } from '@ant-design/icons';
import { Drawer, Select, Input, Tooltip, DatePicker, message } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { RCTable, ButtonWrapper } from 'components';
import { Box, Flex, Text } from 'atoms';
import { ACL, FilterTitle } from 'components/common';
import { getListAction, exportSlice } from 'store/pageSlice';
import { VendorTableConfig, initialPaginateConfig, VendorListQueries } from 'utils';
import { useQueryParams } from 'hooks';
import { FilterAtnButton } from 'utils/commonStyled';

const { Search } = Input;
const { RangePicker } = DatePicker;

const LtSearchBar = styled(Search)`
	border: none;
`;

const allOption = {
	value: '',
	label: 'All',
};

const StatusOptions = [
	{ value: 'true', label: 'Active' },
	{ value: 'false', label: 'Inactive' },
];
StatusOptions.unshift(allOption);

export default function VendorListPage() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [citiesList, setCitiesList] = useState('');
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [rowData, setRowData] = useState({});
	const [rangePickerValue, setRangePickerValue] = useState(null);
	const [paginateConfig, setpaginateConfig] = useQueryParams({
		search_type: 'vendor',
		filterQuery: VendorListQueries,
		filter_by: {
			user_type: 'vendor',
		},
	});
	useEffect(() => {
		dispatch(getListAction(paginateConfig)).then((res) => {
			let { cities = [] } = res;

			cities = cities?.map((element) => ({
				value: element,
				label: element,
			}));
			cities.unshift(allOption);

			setCitiesList(cities);
		});
	}, [dispatch, paginateConfig]);

	// restore date range picker value from url
	useEffect(() => {
		if (paginateConfig?.from_date && paginateConfig?.to_date) {
			setRangePickerValue([
				moment(paginateConfig.from_date).add(5, 'hours').add(30, 'minutes'),
				moment(paginateConfig.to_date).add(5, 'hours').add(30, 'minutes'),
			]);
		}
	}, [paginateConfig?.from_date, paginateConfig?.to_date]);

	// Fetch business units
	useEffect(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
			})
		).then((res) => {
			const buOptions = (res?.rows || []).map((bu) => ({
				value: bu.id,
				label: bu.name,
			}));
			// Don't add "All" option for multiple select filters
			setBusinessUnitsList(buOptions);
		});
	}, [dispatch]);

	// business_unit_ids is now always an array thanks to useQueryParams transformation
	const businessUnitIds = paginateConfig?.business_unit_ids || [];

	const cityFilterValue = paginateConfig?.city ? 'City' : '';
	const StatusValue = paginateConfig?.is_active ? 'Status' : '';
	const businessUnitFilterValue = businessUnitIds.length > 0 ? 'Business Unit' : '';
	const cityAllValue = paginateConfig?.city === '' ? 'City' : '';
	const StatusAllValue = paginateConfig?.is_active === '' ? 'Status' : '';
	const dateFilterValue =
		paginateConfig?.from_date || paginateConfig?.to_date ? 'Date Range' : '';
	const paginationArry = [
		cityFilterValue,
		StatusValue,
		businessUnitFilterValue,
		cityAllValue,
		StatusAllValue,
		dateFilterValue,
	];
	const validArray = paginationArry.filter((n) => n);
	console.log(validArray.length);
	const { search_type, offset, limit, currentPage, ...restPaginateConfig } = paginateConfig;
	const handleExport = () => {
		const data = {
			report_type: 'vendor_export',
			file_type: 'csv',
			data: {
				...restPaginateConfig,
				url_type: 'download',
			},
		};
		dispatch(
			exportSlice({
				data,
			})
		).then(() => {
			if (businessUnitIds.length > 0) {
				message.warning(
					'Export completed. Some records without Business Unit association were excluded'
				);
			} else {
				message.success('Export started successfully.');
			}
		});
	};
	return (
		<>
			<Flex justifyContent="space-between" mt={3}>
				<Box flex="0 0 20%">
					<LtSearchBar
						enterButton="Search"
						placeholder="Search"
						allowClear
						onSearch={(val) =>
							setpaginateConfig({
								...initialPaginateConfig,
								query_str: val,
								search_type: 'vendor',
							})
						}
					/>
				</Box>
				<Box flex="0 0 35%" className="filter-hightlight">
					<Box flex="0 0 35%" display="flex">
						<FilterAtnButton
							onClick={() => {
								setOpen(true);
							}}
						>
							FILTERS
						</FilterAtnButton>
						<span className="highlight-list">
							{StatusValue && <span className="ant-tag">{StatusValue} </span>}
							{cityFilterValue && <span className="ant-tag">{cityFilterValue} </span>}
							{businessUnitFilterValue && (
								<span className="ant-tag" style={{ width: '100px' }}>
									{businessUnitFilterValue}
								</span>
							)}
							{cityAllValue && <span className="ant-tag">{cityAllValue} </span>}
							{StatusAllValue && <span className="ant-tag">{StatusAllValue} </span>}
							{dateFilterValue && <span className="ant-tag">{dateFilterValue} </span>}
							{validArray.length > 0 && (
								<Tooltip title="Clear filter">
									<CloseOutlined
										className="filter-close-icon"
										onClick={() => {
											window.location = `${window.origin}/vendor-list`;
										}}
									/>
								</Tooltip>
							)}
						</span>
					</Box>
				</Box>
				<Flex>
					<ACL action="generate_report" module="vendor_export">
						<ButtonWrapper onClick={handleExport} icons={<ExportOutlined />}>
							EXPORT
						</ButtonWrapper>
					</ACL>
					<ButtonWrapper
						onClick={() =>
							setpaginateConfig({
								...initialPaginateConfig,
								search_type: 'vendor',
							})
						}
						icons={<RedoOutlined />}
					>
						SYNC
					</ButtonWrapper>
					<ACL action="create" module="vendor">
						<ButtonWrapper
							onClick={() => navigate('/vendor/addSection')}
							icons={<PlusOutlined />}
						>
							CREATE
						</ButtonWrapper>
					</ACL>
				</Flex>
			</Flex>
			<Box mt={3}>
				<RCTable
					rowKey="id"
					tableDatas={[rowData, setRowData]}
					togglePaginate={[paginateConfig, setpaginateConfig]}
					dispatchAction={getListAction}
					columns={VendorTableConfig}
				/>
			</Box>

			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px">Date Range</Text>
						<RangePicker
							name="date_filter"
							style={{ width: '100%' }}
							allowClear
							value={rangePickerValue}
							onChange={(val) => {
								setRangePickerValue(val);
								if (val) {
									const fromDate = val[0]
										.startOf('day')
										.subtract(5, 'hours')
										.subtract(30, 'minutes')
										.format('MM/DD/YYYY HH:mm:ss');
									const toDate = val[1]
										.endOf('day')
										.subtract(5, 'hours')
										.subtract(30, 'minutes')
										.format('MM/DD/YYYY HH:mm:ss');
									setpaginateConfig((prev) => ({
										...prev,
										from_date: fromDate,
										to_date: toDate,
									}));
								} else {
									setpaginateConfig((prev) => ({
										...prev,
										from_date: false,
										to_date: false,
									}));
								}
							}}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="status"
							value={paginateConfig?.is_active}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									is_active: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={StatusOptions}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Business Unit</Text>
						<Select
							mode="multiple"
							showSearch
							style={{ width: '100%' }}
							name="business_unit_ids"
							value={businessUnitIds}
							onChange={(val) => {
								setpaginateConfig((prev) => ({
									...prev,
									business_unit_ids: val,
								}));
								if (val.length > 0) {
									message.warning(
										'Adhoc vendors are not linked to business units, hence will be excluded.'
									);
								}
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={businessUnitsList}
							maxTagCount="responsive"
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={paginateConfig?.city}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									city: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={citiesList}
						/>
					</Box>
					<Box>
						{/* <ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() =>
								setpaginateConfig((prev) => ({
									...prev,
									user_type: 'admin',
								}))
							}
						>
							RESET FILTERS
						</ButtonWrapper> */}
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() => {
								// navigate('/client-list');
								window.location = `${window.origin}/vendor-list`;
							}}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>
		</>
	);
}
